// Bookie · Tuproq Guli — Housewarming preview thumbnail. The full design
// lives at /templates/bookie-terracotta.html (Mediterranean-villa
// terracotta on cream, Fraunces display + Newsreader body + Manrope
// UI). For housewarming the card centerpiece is a tiny terracotta
// "house with arched door" mark instead of the wedding couple, the
// occasion line reads "a housewarming dinner", and the family name
// sits where the couple used to.

import type { LayoutCtx } from "./_shared";

export function BookieTerracottaPreviewLayout({ names, date, venue, ink, bg, font }: LayoutCtx) {
  // Hardcode the brand accents so the catalog card always reads
  // "terracotta + cream + small gold" regardless of which palette
  // the row was tagged with upstream.
  const terracotta = "#B5523A";
  const terracottaSoft = "#D27A60";
  const gold = "#B69356";
  const cream = "#FAF3E3";
  const cream2 = "#F4E9D2";

  // Display the host-family name as-is (single string for
  // housewarming — no ampersand split).
  const host = (names || "").trim() || "Karimov oilasi";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${cream} 0%, ${bg} 22%, ${cream2} 100%)`,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "13% 9% 9%",
      }}
    >
      {/* Tiny terracotta house mark — arched door, sloped roof, single
          stroke. Mediterranean villa silhouette. */}
      <svg
        aria-hidden
        viewBox="0 0 64 48"
        style={{ width: "28%", height: "auto", color: terracotta, opacity: 0.92 }}
      >
        {/* Body */}
        <path
          d="M 8 44 L 8 22 L 32 6 L 56 22 L 56 44 Z"
          stroke="currentColor"
          strokeWidth="1.3"
          fill="none"
          strokeLinejoin="round"
        />
        {/* Roof line */}
        <path
          d="M 4 24 L 32 4 L 60 24"
          stroke={gold}
          strokeWidth="0.9"
          fill="none"
          strokeLinecap="round"
        />
        {/* Arched door */}
        <path
          d="M 26 44 L 26 32 A 6 6 0 0 1 38 32 L 38 44"
          stroke="currentColor"
          strokeWidth="1"
          fill="none"
        />
        {/* Window glints */}
        <rect x="14" y="28" width="6" height="6" stroke="currentColor" strokeWidth="0.8" fill="none"/>
        <rect x="44" y="28" width="6" height="6" stroke="currentColor" strokeWidth="0.8" fill="none"/>
        {/* Tiny chimney */}
        <rect x="44" y="12" width="3" height="6" fill="currentColor" opacity="0.85"/>
      </svg>

      {/* Terracotta eyebrow */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          letterSpacing: "0.42em",
          textTransform: "uppercase",
          color: terracotta,
          marginTop: 12,
          fontWeight: 700,
        }}
      >
        YANGI UY BAYRAMI
      </div>

      {/* Headline */}
      <div
        style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
          fontSize: 21,
          lineHeight: 1.05,
          color: ink,
          marginTop: 8,
          maxWidth: "100%",
        }}
      >
        Xush kelibsiz
      </div>

      {/* Gold rule */}
      <div
        aria-hidden
        style={{
          width: 28,
          height: 1,
          background: gold,
          opacity: 0.9,
          margin: "10px 0 6px",
        }}
      />

      {/* Italic Fraunces accent (was Allura on the wedding version) */}
      <div
        style={{
          fontFamily: "'Fraunces', serif",
          fontStyle: "italic",
          fontWeight: 300,
          fontSize: 14,
          color: terracottaSoft,
          lineHeight: 1.2,
        }}
      >
        a housewarming dinner
      </div>

      {/* Host family name */}
      <div
        style={{
          marginTop: 14,
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
          fontSize: 17,
          color: ink,
          lineHeight: 1.2,
          letterSpacing: "0.005em",
        }}
      >
        {host}
      </div>

      {/* Date + venue (footer) */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 12,
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          letterSpacing: "0.32em",
          color: ink,
          opacity: 0.78,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 10,
          color: ink,
          opacity: 0.7,
          marginTop: 2,
        }}
      >
        {venue}
      </div>
    </div>
  );
}
